import React from 'react';
import { observer } from 'mobx-react';
import OETCell from './OETCell';
import type { IOETRow } from '../../stores/model/uimodel/oetrow';
import type { IOETColumn } from '../../stores/model/uimodel/oetcolumn';
import type { IObjectType } from '../../stores/model/metamodel/object_type';

/**
 * The cursor-following drag preview rendered inside dnd-kit's <DragOverlay>.
 *
 * react-dnd's HTML5 backend showed a native drag-image: a full-row screenshot
 * when dragging a row, and (because a column has no DOM subtree) only the
 * header `<th>` when dragging a column. dnd-kit renders no preview by default,
 * so this component reproduces those two previews so the drag ghost follows the
 * cursor exactly as before.
 *
 * The previews are wrapped in their own `<table class="oet-table">` so the OET
 * styles (vertical header text, cell colours, row striping) apply, and so a
 * bare `<tr>`/`<th>` is valid markup. The cells reuse `OETCell`, which is purely
 * presentational (no drag/drop hooks), so nothing double-registers.
 */

/** A faithful preview of a dragged OET row (row header + its cells). */
const RowOverlay = observer(function RowOverlay({
  row,
  columns,
  highlightedMethods,
}: {
  row: IOETRow;
  columns: IOETColumn[];
  highlightedMethods: string[];
}) {
  const classes: string[] = [];
  if (row.event.specialised) {
    classes.push('specialised');
  }
  return (
    <table className="oet-table oet-drag-overlay">
      <tbody>
        <tr>
          <th className={classes.join(' ')}>
            {row.event.specialised && (
              <span className="arrows">
                {/* Mirror the live row's `→ ` arrows (one per specialisation
                    level), kept byte-identical with OETRow. */}
                {/* eslint-disable-next-line prefer-spread */}
                {Array.apply(
                  null,
                  { length: row.event.specialisationLevel } as unknown as unknown[]
                ).map((e, i) => (
                  <span className="arrow" key={i}>
                    {'→ '}
                  </span>
                ))}
              </span>
            )}
            {row.event.name}
          </th>
          {columns.map((c) => (
            <OETCell
              key={c.object.id}
              object={c.object as unknown as IObjectType}
              event={row.event}
              selectable={false}
              onSelect={() => undefined}
              selected={null}
              highlightedMethods={highlightedMethods}
              popovers={false}
              onClick={undefined}
              isDragging={false}
            />
          ))}
        </tr>
      </tbody>
    </table>
  );
});

/** A faithful preview of a dragged OET column header cell. */
const ColumnOverlay = observer(function ColumnOverlay({ column }: { column: IOETColumn }) {
  const classes: string[] = [];
  if (column.object.abstract) {
    // Match the live header cell (OETHeaderCell uses text-grey2). The old scoped
    // `.oet-content .abstract` rule does not reach this portaled overlay clone.
    classes.push('text-grey2');
  }
  return (
    <table className="oet-table oet-drag-overlay">
      <thead>
        <tr>
          <th className={classes.join(' ')}>
            <div className="text-vertical">
              <div className="inner-text">{column.object.name}</div>
            </div>
          </th>
        </tr>
      </thead>
    </table>
  );
});

export interface OETDragOverlayProps {
  row?: IOETRow | null;
  column?: IOETColumn | null;
  columns: IOETColumn[];
  highlightedMethods: string[];
}

/**
 * Render the drag preview for whichever item is active: a row preview when a
 * row is dragged, a header-cell preview when a column is dragged, nothing
 * otherwise.
 */
const OETDragOverlay = observer(function OETDragOverlay({
  row,
  column,
  columns,
  highlightedMethods,
}: OETDragOverlayProps) {
  if (row) {
    return <RowOverlay row={row} columns={columns} highlightedMethods={highlightedMethods} />;
  }
  if (column) {
    return <ColumnOverlay column={column} />;
  }
  return null;
});

export default OETDragOverlay;
